import { useCallback, useEffect, useState } from "react";
import { Badge, Button, Modal, Table, Tag, Tooltip, Alert, message } from "antd";
import { SyncOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  fetchZohoPaymentBackfillStatus,
  runZohoPaymentBackfill,
  ZohoBackfillSummary,
} from "../../apis/procurement.api";
import { getFormattedDateAndTime } from "../../helpers/date_helper";

// Matches the backend's inline cap. A push is a Zoho round trip, so a run is a batch;
// the response reports what is left and the operator clicks again.
const BATCH = 25;

const money = (n: number) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

/**
 * Authorizing a payment pushes it to Zoho Books, but that leg can fail on its own —
 * the money is still ours, Zoho just doesn't know about it. A nightly sweep
 * (`zoho-payments.backfill-nightly`) catches those, which meant finance could see
 * neither how much was outstanding nor close it before tomorrow morning.
 *
 * This is that sweep, on a button, plus the count it would pick up. Both come from
 * the same server-side queryset as the cron, so the badge cannot drift from it.
 */
const ZohoReconcile = ({ canManage }: { canManage: boolean }) => {
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState<ZohoBackfillSummary | null>(null);
  // Distinct from `pending === 0`: a failed request must never read as "all synced".
  const [unreadable, setUnreadable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await fetchZohoPaymentBackfillStatus();
    if (res) {
      setSummary(res);
      setUnreadable(false);
    } else {
      setUnreadable(true);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleRun = async () => {
    if (!canManage) {
      message.error("You don't have permission to push payments to Zoho");
      return;
    }
    setRunning(true);
    const res = await runZohoPaymentBackfill(BATCH);
    setRunning(false);

    if (!res) {
      message.error("Reconcile failed. Nothing was pushed — try again.");
      return;
    }
    if (res.status === "busy") {
      message.info(res.message || "A reconcile is already running.");
      setSummary(res);
      return;
    }

    setSummary(res);
    setUnreadable(false);
    const pushed = res.pushed || 0;
    const remaining = res.remaining ?? 0;
    if (res.errors) {
      message.warning(
        `Pushed ${pushed} to Zoho, ${res.errors} failed. ${remaining} still pending — see the list below.`
      );
    } else if (pushed) {
      message.success(
        `Pushed ${pushed} payment${pushed === 1 ? "" : "s"} to Zoho${
          remaining ? `. ${remaining} still pending — run again.` : ". Everything is synced."
        }`
      );
    } else {
      message.info("Nothing was pushed — these rows were already in Zoho or were skipped.");
    }
  };

  const pending = summary?.pending ?? 0;
  const since = summary?.since
    ? dayjs(summary.since).format("D MMM YYYY")
    : "1 Aug 2026";

  const columns = [
    { title: "Lead ID", dataIndex: "lead_id", render: (v: string) => v || "—" },
    {
      title: "Amount",
      dataIndex: "amount",
      render: (v: number) => money(v),
    },
    {
      title: "Mode",
      dataIndex: "mode",
      render: (v: string) => (v ? <Tag>{v.toUpperCase()}</Tag> : "—"),
    },
    { title: "Medium", dataIndex: "medium", render: (v: string) => v || "—" },
    { title: "Payment ID", dataIndex: "payment_id", render: (v: string) => v || "—" },
    { title: "Status", dataIndex: "status" },
    {
      title: "Taken on",
      dataIndex: "created_at",
      render: (v: string) => (v ? getFormattedDateAndTime(v) : "—"),
    },
  ];

  return (
    <>
      <Tooltip
        title={
          unreadable
            ? "Couldn't check the Zoho sync status"
            : `${pending} payment${pending === 1 ? "" : "s"} since ${since} not yet in Zoho`
        }
      >
        <Badge count={unreadable ? "?" : pending} size="small" offset={[-4, 2]}>
          <Button icon={<SyncOutlined />} onClick={() => setOpen(true)} loading={loading}>
            Zoho Sync
          </Button>
        </Badge>
      </Tooltip>

      <Modal
        title="Zoho payment reconcile"
        open={open}
        onCancel={() => setOpen(false)}
        width={1000}
        footer={[
          <Button key="refresh" onClick={load} loading={loading}>
            Refresh
          </Button>,
          <Button key="close" onClick={() => setOpen(false)}>
            Close
          </Button>,
          <Button
            key="run"
            type="primary"
            onClick={handleRun}
            loading={running}
            disabled={!canManage || unreadable || pending === 0}
          >
            Push {Math.min(pending, BATCH) || ""} to Zoho
          </Button>,
        ]}
      >
        {unreadable ? (
          <Alert
            type="warning"
            showIcon
            message="Couldn't read the Zoho sync status"
            description="This is a connection problem, not a clean slate — there may still be payments waiting. Hit Refresh."
          />
        ) : (
          <>
            <Alert
              type={pending ? "warning" : "success"}
              showIcon
              className="mb-3"
              message={
                pending
                  ? `${pending} authorized payment${pending === 1 ? "" : "s"} worth ${money(
                      summary?.pending_amount || 0
                    )} have not reached Zoho`
                  : `Every authorized payment since ${since} is in Zoho`
              }
              description={
                pending ? (
                  <>
                    Counted from {since}
                    {summary?.oldest_created_at
                      ? `; oldest taken ${getFormattedDateAndTime(summary.oldest_created_at)}`
                      : ""}
                    . These run automatically overnight — pushing here just closes the gap
                    sooner. Up to {BATCH} per click.
                    {summary?.by_medium?.length ? (
                      <div className="mt-2">
                        {summary.by_medium.map((m) => (
                          <Tag key={m.medium}>
                            {m.medium}: {m.count} · {money(m.amount)}
                          </Tag>
                        ))}
                      </div>
                    ) : null}
                  </>
                ) : null
              }
            />
            {pending > 0 && (
              <Table
                size="small"
                rowKey="ept_id"
                columns={columns}
                dataSource={summary?.rows || []}
                pagination={false}
                scroll={{ x: "max-content", y: 380 }}
                footer={() =>
                  pending > (summary?.rows?.length || 0)
                    ? `Showing the oldest ${summary?.rows?.length} of ${pending}`
                    : `${pending} shown`
                }
              />
            )}
          </>
        )}
      </Modal>
    </>
  );
};

export default ZohoReconcile;
